// List page for outreachEmailTemplate rows. Click any row to edit;
// "New template" button to create one. Retired templates are shown
// with a muted badge so we can tell at-a-glance what's still in use.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import client from '../../lib/feathers';

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client.service('admin/outreach-email-template')
      .find({ query: { includeInactive: includeInactive ? '1' : undefined, $limit: 200 } })
      .then((r) => { if (!cancelled) setTemplates(r.data || []); })
      .catch(() => { /* leave list empty */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [includeInactive]);

  return (
    <div className="space-y-4">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Email templates</h2>
            <p className="text-sm text-gray-500 mt-1">
              Reusable bodies + subjects available on every compose page via "Start from template".
            </p>
          </div>
          <Link
            to="/admin/email-templates/new"
            className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700"
          >
            New template
          </Link>
        </div>
        <div className="px-6 py-3 border-b border-gray-100 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            Show retired templates
          </label>
        </div>
        <div className="px-6 py-5">
          {loading && <p className="text-gray-500">Loading…</p>}
          {!loading && templates.length === 0 && (
            <p className="text-gray-500">No templates yet. Create your first one to make composing emails faster.</p>
          )}
          {!loading && templates.length > 0 && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {templates.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm">
                      <Link to={`/admin/email-templates/${t.id}`} className="text-blue-700 hover:underline font-medium">
                        {t.name}
                      </Link>
                      {t.description && <div className="text-xs text-gray-500 mt-0.5">{t.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">{t.subject}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {t.isActive ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">Retired</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {t.updatedAt ? formatDistance(new Date(t.updatedAt), new Date(), { addSuffix: true }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
